import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRoutes, Navigate, useNavigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";

import { analytics } from "services/analytics";
import MainComponent from "components/main/main.component";
import DashBoard from "components/Screens/DashBoard";
import Workforce from "components/Screens/Workforce";
import UserDetails from "components/Screens/Workforce/UserDetailsV2";
import EditSalaryRoute from "components/Screens/Workforce/UserDetailsV2/IndividualEditSalary";
import Payroll from "components/Screens/PayrollV2";
import PayrollDetails from "components/Screens/Payroll/PayrollDetails";
import UpcomingHolidays from "components/Screens/UpcomingHolidays/UpcomingHolidays";
import HolidaySchedule from "components/Screens/HolidaySchedule/HolidaySchedule";
import GetInTouch from "components/Screens/GetInTouch";
import Customers from "components/Screens/Customers";
import Payment from "components/Screens/PaymentV2/PaymentNew";
import PaymentOld from "components/Screens/PaymentV2";
import AddPayment from "components/Screens/PaymentV2/PaymentNew/AddPayment";
import OldSetting from "components/Screens/SettingV2/SettingsV2";
import UserDetail from "components/Screens/Setting/MangeRoles/userDetail";
import Login from "components/Screens/Login";
import SignUp from "components/Screens/Login/SignUp";
import AddEmployee from "components/Screens/Workforce/AdminWorkforce/AddTalent";
import AddCustomer from "components/Screens/Customers/AddCustomer";
import CustomerDetials from "components/Screens/Customers/CustomerDetials";
import AddUser from "components/Screens/Setting/AddUser";
import Profile from "components/Screens/SettingV2/Profile";
import ProfileV2 from "components/Screens/SettingV2/Profile/ProfileV2";
import AddBonus from "components/Screens/Customers/CustomerDetials/AddBonus";
import EditPayroll from "components/Screens/PayrollV2/EditPayroll/index";
import EmployeePayStub from "components/Screens/Employee/EmployeePayStub";
import EmployeePayStubDetails from "components/Screens/Employee/PayslipDetails/EmployeePayStubDetails";
import EmployeeProfile from "components/Screens/Employee/EmployeeProfile";
import EmployeeDocuments from "components/Screens/Employee/EmployeeDocuments";
import CompanyDetails from "components/Screens/Employee/CompanyDetailsv2";
import EmployeePeersList from "components/Screens/Employee/EmployeePeersList";
import ClockOutSuccessModalExample from "components/CheckInCheckOut/ClockOutSuccessModal/ClockOutSuccessModal.example";
import ClockInBlockedCardExample from "components/CheckInCheckOut/ClockInBlockedCard/ClockInBlockedCard.example";
import PayrollHistoryDetail from "components/Screens/PayrollHistoryDetail";
import FirstPaymentPayrollDetails from "components/Screens/PayrollHistoryDetail/FirstPaymentPayrollDetails";
import EmployeeUpComingHolidays from "components/Screens/Employee/EmployeeUpComingHolidays";
import Documents from "components/Screens/Documents";
import Salaries from "components/Screens/Salaries";
import PayrollUpload from "components/Screens/PayrollUpload";
import ChangePassword from "components/Screens/Login/ChangePassword";
import CreatePassword from "components/Screens/CustomerOnboarding/CreatePassword";
import LinkExpired from "components/Screens/CustomerOnboarding/LinkExpired/LinkExpired";
import EmployeeOnboardingLinkExpired from "components/Screens/EmployeeOnboarding/LinkExpired";
import AddPaymentMethodV2 from "components/Screens/Login/AddPaymentMethodV2";
import ResendLink from "components/Screens/Login/ChangePassword/ResendLink";
import LeaveStatus from "components/Screens/Timeoff/LeaveStatus";
import LeaveDetails from "components/Screens/Timeoff/ManageTimeoff/Customer/Details";
import FeedbackScreen from "components/Screens/Feedback/Feedback";
import ManageTimeoff from "components/Screens/Timeoff/ManageTimeoff";
import RequestTimeoff from "components/Screens/Timeoff/ManageTimeoff/RequestTimeoff";
import LeaveTimeoff from "components/Screens/Timeoff/ManageTimeoff/Customer/LeaveTimeOff";
import EmployeePayoneer from "components/Screens/Employee/EmployeePayoneer";
import EdgeAtlas from "components/Screens/EdgeAtlas";
import Timesheet from "components/Screens/Timesheet";

import { GetPlatformConfig } from "store/actions/PlatformConfig";
import { UserAdditionalInfo } from "store/actions/UserAdditionalInfo";

import useRequirePermission from "./useRequirePermission";
import { capitalizeFirstLetter, getAge } from "util/utils";
import TimeoffAdmin from "components/Screens/Timeoff/TimeoffAdmin";
import EmployeeTimeOffDetails from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails";
import EmployeeTimeoffNewActivity from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeoffNewActivity";
import TimeOffUtilisationReport from "components/Screens/Timeoff/TimeoffAdmin/UtilisationReport";
import Chat from "components/Screens/Chat";
import Terms from "components/Screens/TermsAndPolicy/Terms";
import CustomerPrivacyPolicy from "components/Screens/TermsAndPolicy/CustomerPolicy";
import EmployeePrivacyPolicy from "components/Screens/TermsAndPolicy/EmployeePolicy";
import EmployeePeersListRevamped from "components/Screens/Employee/EmployeePeersListRevamped";
import CalendlyMeeting from "components/Screens/CustomerOnboarding/CalendlyMeeting/CalendlyMeeting";
import AddPaymentMethod from "components/Screens/CustomerOnboarding/AddPaymentMethod";
import PaymentSuccess from "components/Screens/CustomerOnboarding/PaymentSuccess";
import MangeRoles from "components/Screens/Setting/MangeRoles";
import TimeOff from "components/Screens/Setting/TimeOff/TimeOff";
import CompanyProfile from "components/Screens/Setting/CompanyProfile";
import SettingsLayout from "components/Screens/Setting/SettingsLayout/SettingsLayout";
import CountryDetailsWrapper from "components/Screens/Setting/CountryDetails/CountryDetailsWrapper";
import PolicyDetailsWrapper from "components/Screens/Setting/PolicyDetails/PolicyDetailsWrapper";
import ResetPassword from "components/Screens/LoginV2/ResetPassword/ResetPassword";
import LoginRebranded from "components/Screens/LoginV2/Login/Login";
import ForgotPassword from "components/Screens/LoginV2/ForgotPassword/ForgotPassword";
import AddCustomerInfo from "components/Screens/Login/SignUp/AddCustomerInfoV2";
import TalentProfilePage from "components/Screens/Marketplace/Customer/ProfilePage/ProfilePage";
import ReferralPage from "components/Screens/Referral";

import { marketplacePrivateRoutes, marketplacePublicRoutes } from "routes/Marketplace.route";
import getUserContext from "util/GetUserContext";
import { getCustomerOnboardingIncompleteStepRoute } from "components/Screens/CustomerOnboarding/helpers";
import { saveIntendedRoute } from "util/postLoginRedirect";

import {
  LOGIN_ROUTE,
  LINK_EXPIRED_ROUTE,
  CUSTOMER_ONBOARDING_CALENDLY_MEETING_ROUTE,
  CUSTOMER_ONBOARDING_PAYMENT_ROUTE,
  CUSTOMER_ONBOARDING_PAYMENT_SUCCESS_ROUTE,
  CUSTOMER_PAYROLL_FIRST_PAYMENT,
  FORGOT_PASSWORD_ROUTE,
  SETTINGS_PATHS,
  ADD_CUSTOMER_INFO_ROUTE,
  EMPLOYEE_ONBOARDING_LINK_EXPIRED_ROUTE,
} from "constants/routes";

import TalentNetworkPage from "components/Screens/Marketplace/Customer/TalentNetwork/TalentNetworkPage";
import RequestAHirePage from "components/Screens/Marketplace/Customer/RequestAHire/RequestAHirePage";

// TODO - to be removed after anniversaryBasedTimeOff is released
const renderSettingsRoute = (anniversaryBasedTimeOff, newRouteElement, redirectPath = "/main/settings") => {
  return anniversaryBasedTimeOff ? newRouteElement : <Navigate to={redirectPath} replace />;
};

function PrivateRoute({ Component }) {
  const accessToken = localStorage.getItem("accessToken");
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const { integratedContractSigning } = useFlags();
  const ldClient = useLDClient();
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const location = useLocation();

  const userAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.userAdditionalInfo);

  let request;
  useEffect(() => {
    dispatch(GetPlatformConfig());
    if (accessToken && loggedInUser && loggedInUser?.type !== "employee") {
      if (loggedInUser?.type == "customer") {
        request = `customer/${loggedInUser.customerId}/info`;
      } else if (loggedInUser?.type == "edge") {
        request = `user/${loggedInUser.userId}/summary`;
      } else if (loggedInUser?.type == "employee") {
        request = `employee/${loggedInUser?.userId}`;
      }
      dispatch(UserAdditionalInfo(request));
    }
  }, []);

  useEffect(() => {
    if (loggedInUser?.email && userAdditionalInfo && Object.keys(userAdditionalInfo).length) {
      /* eslint-disable camelcase */
      const {
        companyName,
        totalEmployees,
        industry,
        city,
        country,
        state,
        dateOfBirth,
        gender,
        peersCount,
        customerInfo,
        jobTitle,
        Community_Profile_Url,
        paymentMethod,
      } = userAdditionalInfo;

      const { name, type, permissions, userId } = loggedInUser;

      const paymentMethodType = {
        CREDIT_CARD: "Credit Card",
        CHEQUE: "Cheque",
        ACH: "ACH",
      };

      let userProps;
      /* eslint-disable camelcase */
      if (type === "customer") {
        userProps = {
          "Full Name": name,
          "Account Name": companyName,
          Industry: industry,
          "No. of Edge Associates": totalEmployees,
          "User Type": capitalizeFirstLetter(type),
          "User Role": permissions,
          "Edge User ID": userId,
          City: city,
          State: state,
          Country: country,
          "Payment Method": paymentMethodType[paymentMethod] || "Missing",
        };
      } else if (type === "employee") {
        userProps = {
          "Full Name": name,
          Age: `${getAge(dateOfBirth)}`,
          Gender: gender,
          "No. of Peers": peersCount,
          "User Type": capitalizeFirstLetter(type),
          "User Role": permissions,
          "Working With": customerInfo?.companyName,
          Industry: customerInfo?.industry,
          Designation: jobTitle,
          "Edge User ID": userId,
          City: city,
          State: state,
          Country: country,
          "Active Customer ID": customerInfo?.customerId,
          "Community Profile Created": !!Community_Profile_Url,
        };
      } else {
        userProps = {
          "Full Name": name,
          "Edge User ID": userId,
          "User Roles": permissions?.join(" - "),
          "User Type": "Admin",
          Designation: userAdditionalInfo?.user?.jobTitle,
        };
      }

      analytics.identify(loggedInUser.email, { ...userProps, email: loggedInUser?.email });
      analytics.setUserProperties(userProps);

      if (window.location.hostname !== "localhost") {
        Sentry.setUser(userProps);
      }
    }
  }, [userAdditionalInfo]);

  useEffect(() => {
    const newUserContext = getUserContext();
    ldClient.identify(newUserContext, null);
  }, []);

  useEffect(() => {
    const { name, type, userId } = loggedInUser || {};

    window.usetifulTags = {
      userId,
      role: type === "customer" ? "Customer" : "Employee",
      firstName: name,
    };

    (function (w, d, s) {
      const a = d.getElementsByTagName("head")[0];
      const r = d.createElement("script");
      r.async = 1;
      r.src = s;
      r.setAttribute("id", "usetifulScript");
      r.dataset.token = "f606cde932518c45e82ccc4011e1313b";
      a.appendChild(r);
    })(window, document, "https://www.usetiful.com/dist/usetiful.js");

    return () => {
      const script = document.getElementById("usetifulScript");
      if (script) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (loggedInUser?.type == "customer" && integratedContractSigning) {
      const { onboardingProgress = {} } = userAdditionalInfo || {};
      const customerOnboardingIncompleteStepRoute = getCustomerOnboardingIncompleteStepRoute(onboardingProgress);
      if (customerOnboardingIncompleteStepRoute) {
        navigateTo(customerOnboardingIncompleteStepRoute);
      }
    }
  }, [userAdditionalInfo]);

  const isAuth = accessToken != null && accessToken != undefined;

  // Deep link bounced to login: remember it so the post-login navigator can send the user back.
  useEffect(() => {
    if (!isAuth) {
      saveIntendedRoute(`${location.pathname}${location.search}${location.hash}`);
    }
  }, [isAuth, location]);

  return isAuth ? <Component /> : <Navigate to="/" />;
}

function PrivatePage(props) {
  const { Component, Permission, type, redirectTo = "/main/dashboard", requirePeruTimeoff = false } = props;
  const isAccessible = useRequirePermission(Permission, type);
  const userAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.userAdditionalInfo);

  const timeoffCountry = userAdditionalInfo?.timeoffCountry;

  if (isAccessible) {
    if (requirePeruTimeoff) {
      if (timeoffCountry !== "Peru") {
        return <Navigate to={redirectTo} />;
      }
    }
    return <Component />;
  }
  return <Navigate to={redirectTo} />;
}

function Index() {
  const {
    teamManagement,
    integratedContractSigning,
    anniversaryBasedTimeOff,
    loginRebrand,
    holidayCalendarRevamp,
    checkinCheckout,
  } = useFlags();
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const isAdmin = loggedInUser?.type === "edge" && loggedInUser?.permissions?.includes("EdgeAdmin");

  return useRoutes([
    { path: LOGIN_ROUTE, element: loginRebrand ? <LoginRebranded /> : <Login /> },
    { path: "/admin", element: <Login /> },
    { path: "/employee", element: loginRebrand ? <LoginRebranded /> : <Login /> },
    { path: "/changePassword", element: loginRebrand ? <ResetPassword /> : <ChangePassword /> },
    { path: "/customerOnboarding/createPassword", element: <CreatePassword /> },
    { path: LINK_EXPIRED_ROUTE, element: <LinkExpired /> },
    { path: "/leaveStatus", element: <LeaveStatus /> },
    { path: "/addPaymentMethod", element: <AddPaymentMethodV2 /> },
    { path: "/signup/:loginPortal", element: <SignUp /> },
    // TODO - Resend link route will be deleted at the time of loginRebrand Flag removal
    { path: "/resend-link", element: <ResendLink /> },
    { path: EMPLOYEE_ONBOARDING_LINK_EXPIRED_ROUTE, element: <EmployeeOnboardingLinkExpired /> },
    { path: "/terms", element: <Terms /> },
    { path: "/policy", element: <CustomerPrivacyPolicy /> },
    { path: "/employee-policy", element: <EmployeePrivacyPolicy /> },
    { path: "marketplace", children: marketplacePublicRoutes },
    { path: FORGOT_PASSWORD_ROUTE, element: <ForgotPassword /> },
    { path: ADD_CUSTOMER_INFO_ROUTE, element: <AddCustomerInfo navigatedFrom="signInScreen" /> },
    {
      path: CUSTOMER_ONBOARDING_CALENDLY_MEETING_ROUTE,
      element: <PrivateRoute Component={CalendlyMeeting} />,
    },

    {
      path: CUSTOMER_ONBOARDING_PAYMENT_ROUTE,
      element: <PrivateRoute Component={AddPaymentMethod} />,
    },
    {
      path: CUSTOMER_ONBOARDING_PAYMENT_SUCCESS_ROUTE,
      element: <PrivateRoute Component={PaymentSuccess} />,
    },
    {
      path: "main",
      element: <PrivateRoute Component={MainComponent} />,
      children: [
        {
          path: "dashboard",
          element: <DashBoard />,
        },
        {
          path: "workforce",
          element: <Workforce />,
        },
        {
          path: "details",
          children: [
            {
              path: ":id",
              element: <UserDetails />,
              children: [
                {
                  path: "editsalary",
                  element: <EditSalaryRoute />,
                },
              ],
            },
          ],
        },
        {
          path: "payroll",
          element: (
            <PrivatePage
              Component={Payroll}
              page="payroll"
              type={["customer"]}
              Permission={["CustomerAdmin", "CustomerEmployee"]}
            />
          ),
        },
        {
          path: "payroll/detail/:id",
          element: (
            <PrivatePage
              Component={PayrollHistoryDetail}
              page="payrollHistoryDetail"
              type={["customer"]}
              Permission={["CustomerAdmin", "CustomerEmployee"]}
            />
          ),
        },
        {
          path: CUSTOMER_PAYROLL_FIRST_PAYMENT,
          element: integratedContractSigning && (
            <PrivatePage
              Component={FirstPaymentPayrollDetails}
              page="firstPaymentPayrollDetails"
              type={["customer"]}
              Permission={["CustomerAdmin", "CustomerEmployee"]}
            />
          ),
        },
        {
          path: "payrollHistory/:id",
          element: (
            <PrivatePage
              Component={PayrollDetails}
              page="payrollHistory"
              type={["customer"]}
              Permission={["CustomerAdmin", "CustomerEmployee"]}
            />
          ),
        },
        {
          path: "settings/manage-users",
          element: renderSettingsRoute(
            anniversaryBasedTimeOff,
            <PrivatePage
              Component={() => (
                <SettingsLayout title="Manage Users" linkName="Manage Users" link={SETTINGS_PATHS.MANAGE_USERS}>
                  <MangeRoles />
                </SettingsLayout>
              )}
              page="settings"
              Permission={["EdgeAdmin"]}
            />
          ),
        },
        {
          path: SETTINGS_PATHS.TIME_OFF,
          element: renderSettingsRoute(
            anniversaryBasedTimeOff,
            <PrivatePage Component={TimeOff} page="settings" Permission={["EdgeAdmin"]} />
          ),
        },
        {
          path: SETTINGS_PATHS.TIME_OFF_COUNTRY,
          element: renderSettingsRoute(
            anniversaryBasedTimeOff,
            <PrivatePage Component={CountryDetailsWrapper} page="settings" Permission={["EdgeAdmin"]} />
          ),
        },
        {
          path: SETTINGS_PATHS.TIME_OFF_POLICY,
          element: renderSettingsRoute(
            anniversaryBasedTimeOff,
            <PrivatePage Component={PolicyDetailsWrapper} page="settings" Permission={["EdgeAdmin"]} />
          ),
        },
        {
          path: "settings/company-profile",
          element: renderSettingsRoute(
            anniversaryBasedTimeOff,
            <PrivatePage
              Component={() => (
                <SettingsLayout
                  title="Company Profile"
                  linkName="Company Profile"
                  link={SETTINGS_PATHS.COMPANY_PROFILE}
                >
                  <CompanyProfile />
                </SettingsLayout>
              )}
              page="settings"
              Permission={["EdgeAdmin", "CustomerAdmin", "CustomerEmployee"]}
            />
          ),
        },
        {
          path: "settings",
          element:
            anniversaryBasedTimeOff && isAdmin ? (
              <Navigate to={SETTINGS_PATHS.MANAGE_USERS} replace />
            ) : (
              <PrivatePage
                Component={OldSetting}
                page="settings"
                Permission={["EdgeAdmin", "CustomerAdmin", "CustomerEmployee"]}
              />
            ),
        },
        {
          path: "customers",
          element: <PrivatePage Component={Customers} page="customers" type={["edge"]} />,
        },
        {
          path: "talent-network/:id",
          element: (
            <PrivatePage Component={TalentProfilePage} page="settings" Permission={["EdgeAdmin", "CustomerAdmin"]} />
          ),
        },

        {
          path: "talent-network",

          element: (
            <PrivatePage
              Component={TalentNetworkPage}
              Permission={["EdgeAdmin", "CustomerAdmin"]}
              page="customers"
              type={["edge", "customer"]}
            />
          ),
        },
        {
          path: "request-a-hire",
          element: (
            <PrivatePage
              Component={RequestAHirePage}
              Permission={["CustomerAdmin"]}
              page="customers"
              type={["customer"]}
            />
          ),
        },
        {
          path: "payment",
          element: <PrivatePage Component={Payment} page="payment" Permission={["CustomerAdmin"]} />,
        },
        {
          path: "paymentold",
          element: <PrivatePage Component={PaymentOld} page="payment" Permission={["CustomerAdmin"]} />,
        },
        {
          path: "payment/add-payment-method",
          element: <PrivatePage Component={AddPayment} page="payment" Permission={["CustomerAdmin"]} />,
        },

        {
          path: "documents",
          element: (
            <PrivatePage
              Component={Documents}
              page="documents"
              Permission={["EdgeAdmin", "CustomerAdmin", "CustomerEmployee"]}
            />
          ),
        },

        {
          path: "workforcepayrolls",
          element: <PrivatePage Component={Salaries} page="salaries" Permission={["EdgeAdmin"]} />,
        },

        {
          path: "payrollsupload",
          element: <PrivatePage Component={PayrollUpload} page="payrollsupload" Permission={["EdgeAdmin"]} />,
        },

        {
          path: "user/details",
          element: <UserDetail />,
          children: [{ path: ":id", element: <UserDetail /> }],
        },
        {
          path: "adduser",
          element: <AddUser />,
        },
        {
          path: "addemployee",
          element: <AddEmployee />,
        },
        {
          path: "addcustomer",
          element: <AddCustomer />,
        },

        {
          path: "customers/addBonus",
          element: <AddBonus />,
        },
        {
          path: "customers/editPayroll",
          element: (
            <PrivatePage
              Component={EditPayroll}
              page="editPayroll"
              type={["customer"]}
              Permission={["CustomerAdmin", "CustomerEmployee"]}
            />
          ),
        },
        {
          path: "customer/details",
          element: <CustomerDetials />,
          children: [{ path: ":id", element: <CustomerDetials /> }],
        },
        {
          path: "profile",
          element: teamManagement ? (
            <PrivatePage Component={ProfileV2} page="profile" type={["edge", "customer"]} />
          ) : (
            <PrivatePage Component={Profile} page="profile" type={["edge", "customer"]} />
          ),
        },
        {
          path: "paystubs/:id",
          element: <PrivatePage Component={EmployeePayStubDetails} page="paystubs" type={["employee"]} />,
        },
        {
          path: "paystubs",
          element: <PrivatePage Component={EmployeePayStub} page="paystubs" type={["employee"]} />,
        },
        {
          path: "eprofile",
          element: <PrivatePage Component={EmployeeProfile} page="paystubs" type={["employee"]} />,
        },

        {
          path: "edocuments",
          element: <PrivatePage Component={EmployeeDocuments} page="paystubs" type={["employee"]} />,
        },
        {
          path: "edge-wallet",
          element: <PrivatePage Component={EmployeePayoneer} page="paystubs" type={["employee"]} />,
        },
        {
          path: "clock-out-success-example",
          element: checkinCheckout && (
            <PrivatePage Component={ClockOutSuccessModalExample} page="paystubs" type={["employee"]} />
          ),
        },
        {
          path: "clock-in-blocked-example",
          element: checkinCheckout && (
            <PrivatePage Component={ClockInBlockedCardExample} page="paystubs" type={["employee"]} />
          ),
        },
        {
          path: "peers",
          element: (
            <PrivatePage
              Component={teamManagement ? EmployeePeersListRevamped : EmployeePeersList}
              page="paystubs"
              type={["employee"]}
            />
          ),
        },
        {
          path: "timeoff/leave",
          element: <PrivatePage Component={LeaveTimeoff} page="timeoff" type={["customer"]} />,
        },
        {
          path: "timeoff",
          element: <PrivatePage Component={ManageTimeoff} page="timeoff" type={["employee"]} />,
        },
        {
          path: "timeoff/request",
          element: <PrivatePage Component={RequestTimeoff} page="timeoff" type={["employee"]} />,
        },
        {
          path: "timeoff/admin",
          element: <PrivatePage Component={TimeoffAdmin} page="timeoff" type={["edge"]} />,
        },
        {
          path: "details/timeoff",
          element: <PrivatePage Component={LeaveDetails} page="timeoff" type={["customer"]} />,
        },
        {
          path: "admin/timeoff/new-activity",
          element: <PrivatePage Component={EmployeeTimeoffNewActivity} page="timeoff" type={["edge"]} />,
        },
        {
          path: "timeoff/admin/Utilisation-report",
          element: <PrivatePage Component={TimeOffUtilisationReport} page="timeoff" type={["edge"]} />,
        },

        {
          path: "timeoff/admin/employee",
          element: <PrivatePage Component={EmployeeTimeOffDetails} page="timeoff" type={["edge"]} />,
        },

        {
          path: "upcoming",
          element: <PrivatePage Component={EmployeeUpComingHolidays} page="holidays" type={["employee", "customer"]} />,
        },
        {
          path: "holidays",
          element: holidayCalendarRevamp ? (
            <PrivatePage Component={HolidaySchedule} page="holidays" type={["employee", "customer"]} />
          ) : (
            <UpcomingHolidays />
          ),
        },
        {
          path: "contact",
          element: <GetInTouch />,
        },
        {
          path: "company",
          element: <PrivatePage Component={CompanyDetails} page="paystubs" type={["employee"]} />,
        },
        {
          path: "feedback",
          element: <PrivatePage Component={FeedbackScreen} page="feedback" type={["employee", "customer"]} />,
        },
        {
          path: "referrals",
          element: <PrivatePage Component={ReferralPage} page="referrals" type={["customer"]} />,
        },
        {
          path: "edge-atlas",
          element: <PrivatePage Component={EdgeAtlas} page="edge-atlas" type={["employee", "customer"]} />,
        },
        {
          path: "timesheet",
          element: checkinCheckout && <PrivatePage Component={Timesheet} page="timesheet" type={["employee"]} />,
        },
        {
          path: "chat",
          element: <PrivatePage Component={Chat} page="chat" type={["employee", "customer", "edge"]} />,
        },
        {
          path: "marketplace",
          children: marketplacePrivateRoutes,
        },
      ],
    },
  ]);
}

export default Index;
